import { useState, useEffect, useCallback } from 'react'
import {
  Users, Shield, UserPlus, Trash2, Edit, Save, X, RefreshCw,
  AlertTriangle, Key, Mail, User, Search
} from 'lucide-react'
import { adminApi } from '../services/ApiService'
import { useAuth } from '../context/AuthContext'
import { SECTIONS_META, ROLE_DEFAULTS, mergeRolePermissions } from '../constants/permissions'
import './AdminUsersHub.css'

const ADMIN_ROLES = {
  superadmin: { label: 'Super Admin', color: '#ef4444', desc: 'Acceso total al sistema' },
  admin:      { label: 'Admin', color: '#3b82f6', desc: 'Administración general' },
  soporte:    { label: 'Soporte', color: '#10b981', desc: 'Tickets, calendario, clientes' },
  ventas:     { label: 'Ventas', color: '#f59e0b', desc: 'Pipeline, presupuestos, facturación' },
  viewer:     { label: 'Solo Lectura', color: '#6b7280', desc: 'Solo ver datos, sin editar' },
  reseller:   { label: 'Reseller', color: '#a855f7', desc: 'Portal de revendedor externo' },
}

export default function AdminUsersHub() {
  const { user: currentUser } = useAuth()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({ email: '', roles: ['admin'], permissions: [...ROLE_DEFAULTS.admin] })
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  // ── Cognito users search ──
  const [allUsers, setAllUsers] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [searchLoading, setSearchLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await adminApi.get('/admin/admin-users')
      setUsers(res.users || [])
    } catch (e) { console.error('loadAdminUsers:', e) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadUsers() }, [loadUsers])

  // Load all Cognito users when form opens (for new user)
  const searchUsers = useCallback(async (query) => {
    setSearchLoading(true)
    try {
      const params = query ? `?search=${encodeURIComponent(query)}&limit=20` : '?limit=50'
      const res = await adminApi.get(`/admin/users${params}`)
      setAllUsers(res.users || [])
    } catch (e) { console.error('searchUsers:', e) }
    finally { setSearchLoading(false) }
  }, [])

  useEffect(() => {
    if (showForm && !editingId) searchUsers('')
  }, [showForm, editingId, searchUsers])

  useEffect(() => {
    if (!showForm || editingId) return
    const timer = setTimeout(() => { if (searchQuery) searchUsers(searchQuery) }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery, showForm, editingId, searchUsers])

  const selectUser = (u) => {
    setForm(f => ({ ...f, email: u.email }))
    setSearchQuery(u.email)
    setShowDropdown(false)
  }

  const handleRoleChange = (role) => {
    setForm(f => {
      let newRoles
      if (f.roles.includes(role)) {
        // Remove role (but don't allow empty)
        newRoles = f.roles.filter(r => r !== role)
        if (newRoles.length === 0) return f // at least one role required
      } else {
        newRoles = [...f.roles, role]
      }
      return { ...f, roles: newRoles, permissions: mergeRolePermissions(newRoles) }
    })
  }

  const togglePermission = (section) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(section)
        ? f.permissions.filter(p => p !== section)
        : [...f.permissions, section]
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.email || form.roles.length === 0) return
    setProcessing(true)
    setError(null)
    try {
      const data = { email: form.email, roles: form.roles, permissions: JSON.stringify(form.permissions) }
      if (editingId) {
        await adminApi.patch(`/admin/admin-users/${editingId}`, data)
      } else {
        const res = await adminApi.post('/admin/admin-users', data)
        if (res.error) { setError(res.error); setProcessing(false); return }
      }
      setShowForm(false)
      setEditingId(null)
      setForm({ email: '', roles: ['admin'], permissions: [...ROLE_DEFAULTS.admin] })
      await loadUsers()
    } catch (e) {
      const msg = e?.response?.error || e?.message || 'Error al guardar'
      setError(msg)
      console.error('saveAdminUser:', e)
    }
    finally { setProcessing(false) }
  }

  const handleEdit = (u) => {
    const roles = u.roles || [u.role || 'admin']
    setForm({
      email: u.email || '',
      roles,
      permissions: u.permissions ? JSON.parse(u.permissions) : mergeRolePermissions(roles),
    })
    setEditingId(u.$id)
    setShowForm(true)
    setError(null)
  }

  const handleDelete = async (id, role) => {
    const label = role === 'reseller' ? 'reseller' : 'admin'
    if (!confirm(`¿Quitar permisos de ${label} a este usuario?`)) return
    try {
      await adminApi.delete(`/admin/admin-users/${id}`)
      await loadUsers()
    } catch (e) { console.error('deleteAdminUser:', e) }
  }

  return (
    <div className="au-page">
      <div className="au-header">
        <div className="au-title-row">
          <Shield size={28} />
          <div>
            <h1>Usuarios Admin</h1>
            <p className="au-subtitle">Gestión de acceso y permisos del panel administrador</p>
          </div>
        </div>
        <div className="au-header-actions">
          <button className="au-btn primary" onClick={() => { setShowForm(!showForm); setEditingId(null); setSearchQuery(''); setShowDropdown(false); setForm({ email: '', roles: ['admin'], permissions: [...ROLE_DEFAULTS.admin] }) }}>
            {showForm ? <><X size={16} /> Cancelar</> : <><UserPlus size={16} /> Agregar Admin</>}
          </button>
        </div>
      </div>

      {showForm && (
        <form className="au-form" onSubmit={handleSubmit}>
          <h3>{editingId ? 'Editar Permisos de Admin' : 'Agregar Admin Existente'}</h3>
          {!editingId && <p className="au-form-hint">Ingresá el email de un usuario ya registrado en la plataforma para darle permisos de admin.</p>}
          {error && <div className="au-error"><AlertTriangle size={14} /> {error}</div>}
          <div className="au-form-grid">
            <div className="au-field">
              <label><Mail size={14} /> Usuario *</label>
              {editingId ? (
                <input type="email" value={form.email} disabled />
              ) : (
                <div className="au-user-search">
                  <div className="au-search-input-wrap">
                    <Search size={14} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={e => { setSearchQuery(e.target.value); setShowDropdown(true); setForm(f => ({ ...f, email: '' })) }}
                      onFocus={() => setShowDropdown(true)}
                      placeholder="Buscar por nombre o email..."
                      autoComplete="off"
                    />
                    {searchLoading && <RefreshCw size={14} className="spin" />}
                  </div>
                  {showDropdown && allUsers.length > 0 && (
                    <div className="au-user-dropdown">
                      {allUsers.filter(u => {
                        if (!searchQuery) return true
                        const q = searchQuery.toLowerCase()
                        return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                      }).map(u => {
                        const isAlreadyAdmin = u.isAdmin
                        const existingRole = isAlreadyAdmin ? (ADMIN_ROLES[u.role]?.label || u.role) : null
                        return (
                          <div
                            key={u.$id}
                            className={`au-user-option ${isAlreadyAdmin ? 'is-admin' : ''} ${form.email === u.email ? 'selected' : ''}`}
                            onClick={() => selectUser(u)}
                          >
                            <div className="au-option-avatar">
                              <User size={14} />
                            </div>
                            <div className="au-option-info">
                              <span className="au-option-name">{u.name || u.email}</span>
                              {u.name && <span className="au-option-email">{u.email}</span>}
                            </div>
                            {existingRole && (
                              <span className="au-option-badge" style={{ color: ADMIN_ROLES[u.role]?.color || '#6b7280' }}>
                                {existingRole}
                              </span>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="au-field">
              <label><Key size={14} /> Roles *</label>
              <div className="au-roles-checkboxes">
                {Object.entries(ADMIN_ROLES).map(([k, v]) => (
                  <label key={k} className={`au-role-checkbox ${form.roles.includes(k) ? 'active' : ''}`} style={{ '--role-color': v.color }}>
                    <input type="checkbox" checked={form.roles.includes(k)} onChange={() => handleRoleChange(k)} />
                    <span className="au-role-dot-sm" style={{ background: v.color }} />
                    <span>{v.label}</span>
                  </label>
                ))}
              </div>
              {form.roles.length > 1 && <p className="au-form-hint" style={{ marginTop: 4 }}>El usuario tendrá acceso a múltiples roles simultáneamente.</p>}
            </div>
          </div>

          <div className="au-permissions">
            <h4>Permisos por Sección</h4>
            {form.roles.length === 1 && form.roles[0] === 'reseller' ? (
              <p className="au-perm-desc">Los resellers acceden exclusivamente a su portal de revendedor. No tienen acceso a secciones del panel admin.</p>
            ) : (
              <>
                <p className="au-perm-desc">El rol seleccionado pre-configura los permisos. Podés personalizarlos.</p>
                <div className="au-perm-grid">
                  {SECTIONS_META.map(s => (
                    <label key={s.key} className={`au-perm-item ${form.permissions.includes(s.key) ? 'enabled' : ''}`}>
                      <input type="checkbox" checked={form.permissions.includes(s.key)} onChange={() => togglePermission(s.key)} />
                      <span>{s.label}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          <button type="submit" className="au-btn primary" disabled={processing}>
            <Save size={16} /> {processing ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear Usuario'}
          </button>
        </form>
      )}

      <div className="au-roles-legend">
        {Object.entries(ADMIN_ROLES).map(([k, v]) => (
          <div key={k} className="au-role-badge-info">
            <span className="au-role-dot" style={{ background: v.color }} />
            <span className="au-role-name">{v.label}</span>
            <span className="au-role-desc">{v.desc}</span>
          </div>
        ))}
      </div>

      <div className="au-users-list">
        {loading ? (
          <div className="au-loading"><RefreshCw size={20} className="spin" /> Cargando...</div>
        ) : users.length === 0 ? (
          <div className="au-empty"><Users size={40} /><p>No hay otros usuarios admin configurados</p><p className="au-empty-hint">Tu usuario actual ({currentUser?.email}) tiene acceso por grupo de Cognito. Agregá más admins buscando usuarios existentes.</p></div>
        ) : (
          users.map(u => {
            const roles = u.roles || [u.role || 'admin']
            const primaryRole = roles.find(r => r !== 'reseller') || roles[0]
            const roleInfo = ADMIN_ROLES[primaryRole] || ADMIN_ROLES.admin
            const perms = u.permissions ? JSON.parse(u.permissions) : []
            return (
              <div key={u.$id} className="au-user-card">
                <div className="au-user-avatar" style={{ background: `${roleInfo.color}20`, color: roleInfo.color }}>
                  <User size={20} />
                </div>
                <div className="au-user-info">
                  <div className="au-user-name">{u.name || u.email}</div>
                  <div className="au-user-email">{u.email}</div>
                  <div className="au-user-perms">
                    {roles.map(r => {
                      const ri = ADMIN_ROLES[r] || ADMIN_ROLES.admin
                      return <span key={r} className="au-role-badge" style={{ background: `${ri.color}20`, color: ri.color }}>{ri.label}</span>
                    })}
                    <span className="au-perm-count">{perms.length} secciones</span>
                  </div>
                </div>
                <div className="au-user-actions">
                  <button className="au-icon-btn" title="Editar" onClick={() => handleEdit(u)}><Edit size={14} /></button>
                  <button className="au-icon-btn danger" title="Eliminar" onClick={() => handleDelete(u.$id, u.role)}><Trash2 size={14} /></button>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
